import React, { useEffect, useState } from "react";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Badge from "@mui/material/Badge";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Menu, MenuItem, Skeleton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import InputDialog from "./InputDialog.tsx";
import EditDialog from "./EditDialog.tsx";
import ConfirmDialog from "./ConfirmDialog.tsx";
import Notification from "./Notification.tsx";
import { MessageGroup, MessageItem, CollaborationInstance } from "../../models/LeftPanel";
import { srOnly } from "../../utils/a11y.ts";
import "../../styles/LeftPanel.css";
import { createChat, createInstance, renameInstance, deleteInstance } from "../../resources/api.resource.ts";
import { UserMapValue } from "../../models/User.ts";
import { useQueryClient } from "@tanstack/react-query";
import { EMPTY_INSTANCE_TOPIC, findReusableNewChatInstance } from "../../utils/newChatInstance.ts";

interface LeftPanelProps {
  MessagesArray: Array<MessageGroup>;
  onSelectItem: (item: MessageItem, sectionName: string) => void;
  onGroupCreated?: (groupData: MessageItem) => void;
  onInstanceSelect?: (instance: CollaborationInstance | null) => void;
  onInstanceCreated?: (newInstance: CollaborationInstance, parentItem: MessageItem, sectionName: string) => void;
  onInstanceRenamed?: (instance: CollaborationInstance, newTopic: string, parentItem: MessageItem, sectionName: string) => void;
  onInstanceDeleted?: (instanceId: string, parentItem: MessageItem, sectionName: string) => void;
  allUsersMap: Record<string, UserMapValue>;
  currentUser: any;
  selectedItem: any;
  isLoading?: boolean;
  hideEmptySections?: boolean;
  assistantOnly?: boolean;
  onMentionCleared?: (collaborationId: string, collaborationIdSummary?: string) => void;
  draftGroupIds?: Set<string>;
  onStartNewDM?: () => void;
  onStartNewGroup?: () => void;
}

const LeftPanel = ({
  MessagesArray,
  onSelectItem,
  onGroupCreated,
  onInstanceSelect,
  onInstanceCreated,
  onInstanceRenamed,
  onInstanceDeleted,
  allUsersMap,
  currentUser,
  selectedItem,
  isLoading,
  hideEmptySections = false,
  assistantOnly = false,
  onMentionCleared,
  draftGroupIds,
  onStartNewDM,
  onStartNewGroup,
}: LeftPanelProps) => {
  const theme = useTheme();
  const nav = theme.palette.navigation;
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<MessageGroup[]>([]);
  const [menuOpen, setMenuOpen] = useState<boolean[]>([]);
  const [idSelected, setIdSelected] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notif, setNotif] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "info" | "warning" | "error",
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const [expandedInstances, setExpandedInstances] = useState<Set<string>>(new Set());
  const [visibleInstancesCount, setVisibleInstancesCount] = useState<Record<string, number>>({});
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const lastAutoExpandedRef = React.useRef<string | null>(null);

  const [isCreatingInstance, setIsCreatingInstance] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [instanceToRename, setInstanceToRename] = useState<{
    instance: CollaborationInstance;
    parentItem: MessageItem;
    sectionName: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState<{
    instance: CollaborationInstance;
    parentItem: MessageItem;
    sectionName: string;
  } | null>(null);

  useEffect(() => {
    if(selectedItem === null) {
      setIdSelected(null);
      setSelectedInstanceId(null);
      lastAutoExpandedRef.current = null;
    } else if(selectedItem?.collaborationId) {
      setIdSelected(selectedItem.collaborationId);

      if (lastAutoExpandedRef.current === selectedItem.collaborationId) {
        return;
      }

      let foundItem: MessageItem | null = null;
      let foundSection = '';

      for (const group of messages) {
        const item = group.items.find(
          i => i.collaborationId === selectedItem.collaborationId ||
               i.collaborationIdSummary === selectedItem.collaborationId ||
               (i.collab_instances || []).some(inst => inst.id === selectedItem.collaborationId)
        );
        if (item) {
          foundItem = item;
          foundSection = group.name;
          break;
        }
      }

      if (foundItem && allowsMultipleInstances(foundItem)) {
        const expansionKey = getExpansionKey(foundItem);

        const matchingInstance = (foundItem.collab_instances || []).find(
          inst => inst.id === selectedItem.collaborationId
        );

        if (matchingInstance) {
          setExpandedInstances(prev => {
            const newSet = new Set(prev);
            newSet.add(expansionKey);
            return newSet;
          });
          setSelectedInstanceId(matchingInstance.id);
          const instanceItem: MessageItem = {
            ...foundItem,
            collaborationId: matchingInstance.id,
            name: matchingInstance.topic || foundItem.name,
          };
          onSelectItem(instanceItem, foundSection);
          onInstanceSelect?.(matchingInstance);
          lastAutoExpandedRef.current = selectedItem.collaborationId;
        } else if (foundItem.collab_instances && foundItem.collab_instances.length > 0) {
          setExpandedInstances(prev => {
            const newSet = new Set(prev);
            newSet.add(expansionKey);
            return newSet;
          });
          const firstInstance = foundItem.collab_instances[0];
          setSelectedInstanceId(firstInstance.id);
          const instanceItem: MessageItem = {
            ...foundItem,
            collaborationId: firstInstance.id,
            name: firstInstance.topic || foundItem.name,
          };
          onSelectItem(instanceItem, foundSection);
          onInstanceSelect?.(firstInstance);
          lastAutoExpandedRef.current = selectedItem.collaborationId;
        }
      }
    }
  }, [selectedItem, messages]);

  useEffect(() => {
    const defaults: MessageGroup[] = [
      { name: "Direct Messages", items: [] },
      { name: "Groups", items: [] },
    ];

    if (MessagesArray && MessagesArray.length > 0) {
      MessagesArray.forEach((group) => {
        const section = defaults.find((s) => s.name === group.name);
        if (section) section.items.push(...group.items);
        else defaults.push(group);
      });
    }

    const dmSection = defaults.find((s) => s.name === "Direct Messages");
    if (dmSection && dmSection.items.length > 0) {
      dmSection.items.sort((a, b) => {
        const aUser = Object.values(allUsersMap).find(
          (user: any) => user.name?.toLowerCase() === a.name?.toLowerCase()
        );
        const aHasAgent = aUser?.userType === "agent";
        const bUser = Object.values(allUsersMap).find(
          (user: any) => user.name?.toLowerCase() === b.name?.toLowerCase()
        );
        const bHasAgent = bUser?.userType === "agent";
        if (aHasAgent && !bHasAgent) return -1;
        if (!aHasAgent && bHasAgent) return 1;
        return 0;
      });
    }

    setMessages(defaults);
    if(menuOpen.length === 0) {
      setMenuOpen(defaults.map(() => true));
    }
  }, [MessagesArray, allUsersMap]);

  const handleCollapse = (index: number) =>
    setMenuOpen((prev) => prev.map((c, i) => (i === index ? !c : c)));

  const getExpansionKey = (item: MessageItem): string =>
    item.collaborationIdSummary || item.collaborationId;

  const handleClickPanel = (item: MessageItem, sectionName: string) => {
    setIdSelected(item.collaborationId);

    if (allowsMultipleInstances(item) && item.collab_instances && item.collab_instances.length > 0) {
      const expansionKey = getExpansionKey(item);
      setExpandedInstances(prev => {
        const newSet = new Set(prev);
        newSet.add(expansionKey);
        return newSet;
      });
      return;
    }

    setSelectedInstanceId(null);
    onSelectItem(item, sectionName);
    onInstanceSelect?.(null);
  };

  const allowsMultipleInstances = (item: MessageItem): boolean => {
    if (item.memberUids && item.memberUids.length > 0) {
      return item.memberUids.some(uid => {
        const user = allUsersMap[uid.toLowerCase()];
        return user?.userType === 'agent';
      });
    }
    const userName = item.name?.toLowerCase();
    const user = Object.values(allUsersMap).find(
      (u: UserMapValue) => u.name?.toLowerCase() === userName
    );
    return user?.userType === 'agent';
  };

  const toggleInstancesExpansion = (expansionKey: string) => {
    setExpandedInstances(prev => {
      const newSet = new Set(prev);
      if (newSet.has(expansionKey)) {
        newSet.delete(expansionKey);
      } else {
        newSet.add(expansionKey);
      }
      return newSet;
    });
  };

  const handleInstanceClick = (item: MessageItem, instance: CollaborationInstance, sectionName: string) => {
    setSelectedInstanceId(instance.id);
    const instanceItem: MessageItem = {
      ...item,
      collaborationId: instance.id,
      name: item.name,
    };
    onSelectItem(instanceItem, sectionName);
    onInstanceSelect?.(instance);
    if (item.hasMention) {
      onMentionCleared?.(item.collaborationId, item.collaborationIdSummary);
    }
  };

  const handleCreateInstance = async (item: MessageItem, sectionName: string) => {
    if (isCreatingInstance) return;

    // If an empty "New chat" already exists, move the user there instead of
    // creating another one on every click.
    const existingEmpty = findReusableNewChatInstance(queryClient, item);
    if (existingEmpty) {
      const expansionKey = getExpansionKey(item);
      setExpandedInstances(prev => {
        const newSet = new Set(prev);
        newSet.add(expansionKey);
        return newSet;
      });
      handleInstanceClick(item, existingEmpty, sectionName);
      return;
    }

    setIsCreatingInstance(true);
    const collaborationIdSummary = item.collaborationIdSummary || item.collaborationId;
    try {
      const response = await createInstance({
        collaborationId: collaborationIdSummary,
        topic: EMPTY_INSTANCE_TOPIC
      });

      const newInstance: CollaborationInstance = {
        id: response.id || response.instanceId,
        topic: response.topic || EMPTY_INSTANCE_TOPIC,
        status: response.status || 'active'
      };

      setSelectedInstanceId(newInstance.id);
      setIdSelected(newInstance.id);

      const expansionKey = getExpansionKey(item);
      setExpandedInstances(prev => {
        const newSet = new Set(prev);
        newSet.add(expansionKey);
        return newSet;
      });

      setNotif({ open: true, message: 'New chat created successfully', severity: 'success' });
      onInstanceCreated?.(newInstance, item, sectionName);
    } catch (err: any) {
      setNotif({ open: true, message: err.message || 'Failed to create new chat', severity: 'error' });
    } finally {
      setIsCreatingInstance(false);
    }
  };

  const handleOpenRenameDialog = (
    e: React.MouseEvent,
    instance: CollaborationInstance,
    parentItem: MessageItem,
    sectionName: string
  ) => {
    e.stopPropagation();
    setInstanceToRename({ instance, parentItem, sectionName });
    setRenameDialogOpen(true);
  };

  const handleRenameInstance = async (newTopic: string) => {
    if (!instanceToRename) return;
    const { instance, parentItem, sectionName } = instanceToRename;
    const collaborationId = parentItem.collaborationIdSummary || parentItem.collaborationId;
    try {
      await renameInstance({ collaborationId, collabInstanceId: instance.id, topic: newTopic });
      setMessages(prevMessages => prevMessages.map(group => {
        if (group.name !== sectionName) return group;
        return {
          ...group,
          items: group.items.map(item => {
            const itemKey = item.collaborationIdSummary || item.collaborationId;
            const parentKey = parentItem.collaborationIdSummary || parentItem.collaborationId;
            if (itemKey !== parentKey) return item;
            return {
              ...item,
              collab_instances: (item.collab_instances || []).map(inst =>
                inst.id === instance.id ? { ...inst, topic: newTopic } : inst
              )
            };
          })
        };
      }));
      setNotif({ open: true, message: 'Chat renamed successfully', severity: 'success' });
      onInstanceRenamed?.({ ...instance, topic: newTopic }, newTopic, parentItem, sectionName);
    } catch (err: any) {
      setNotif({ open: true, message: err.message || 'Failed to rename chat', severity: 'error' });
    } finally {
      setRenameDialogOpen(false);
      setInstanceToRename(null);
    }
  };

  const handleOpenDeleteDialog = (
    e: React.MouseEvent,
    instance: CollaborationInstance,
    parentItem: MessageItem,
    sectionName: string
  ) => {
    e.stopPropagation();
    setInstanceToDelete({ instance, parentItem, sectionName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteInstance = async () => {
    if (!instanceToDelete) return;
    const { instance, parentItem, sectionName } = instanceToDelete;
    const collaborationId = parentItem.collaborationIdSummary || parentItem.collaborationId;
    const wasSelected = selectedInstanceId === instance.id;
    try {
      await deleteInstance(collaborationId, instance.id);
      setMessages(prevMessages => prevMessages.map(group => {
        if (group.name !== sectionName) return group;
        return {
          ...group,
          items: group.items.map(item => {
            const itemKey = item.collaborationIdSummary || item.collaborationId;
            const parentKey = parentItem.collaborationIdSummary || parentItem.collaborationId;
            if (itemKey !== parentKey) return item;
            const remainingInstances = (item.collab_instances || []).filter(
              inst => inst.id !== instance.id
            );
            const newCount = remainingInstances.reduce((sum, inst) => sum + (inst.status === 'active' ? (inst.unreadCount || 0) : 0), 0);
            if (wasSelected && remainingInstances.length > 0) {
              setSelectedInstanceId(remainingInstances[0].id);
              handleInstanceClick(item, remainingInstances[0], sectionName);
            } else if (wasSelected && remainingInstances.length === 0) {
              setSelectedInstanceId(null);
              onInstanceSelect?.(null);
            }
            return { ...item, collab_instances: remainingInstances, notificationCount: newCount, unread: newCount > 0 };
          })
        };
      }));
      setNotif({ open: true, message: 'Chat deleted successfully', severity: 'success' });
      onInstanceDeleted?.(instance.id, parentItem, sectionName);
    } catch (err: any) {
      setNotif({ open: true, message: err.message || 'Failed to delete chat', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setInstanceToDelete(null);
    }
  };

  const handleMenuClick = (section: string, e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setCurrentSection(section);
  };

  const handleMenuAction = (action: string) => {
    if (action === "Start DM" && onStartNewDM) {
      onStartNewDM();
    } else if (action === "Create Group") {
      setDialogOpen(true);
    }
    setMenuAnchor(null);
  };

  const handleDialogSubmit = async (name: string, members: string[]) => {
    try {
      const type = currentSection === "Direct Messages" ? "dm" : "group";

      const memberDetails = members.map((uid) => ({
        uid,
        name: allUsersMap[uid.toLowerCase()]?.name || "Unknown",
        email: allUsersMap[uid.toLowerCase()]?.email || "unknown@example.com",
        userType: allUsersMap[uid.toLowerCase()]?.userType || "user",
      }));

      const payload: any = { type, memberUids: members, memberDetails };
      if (type !== "dm") payload.name = name;

      const res = await createChat(payload);
      const data = await res;
      const sectionName = currentSection || "Groups";

      const newItem: MessageItem = {
        collaborationId: data.collaborationId,
        groupId: data.groupId || data.collaborationId,
        name: data.name || name,
        members: data.members || members,
        iconName: type === "group" ? "GroupsIcon" : "PersonIcon",
        notificationCount: 0,
        unread: false,
        avatar: undefined,
        selected: false,
      };

      if (onGroupCreated) onGroupCreated(newItem);
      setDialogOpen(false);
      setNotif({ open: true, message: `${name} created successfully!`, severity: "success" });
    } catch (err: any) {
      setNotif({ open: true, message: err.message || "Failed to create collaboration", severity: "error" });
    }
  };

  const getMenuOptions = (section: string) => {
    if (section === "Groups") return ["Create Group"];
    if (section === "Direct Messages") return ["Start DM"];
    return [];
  };

  const getTitle = (name: string) => {
    const isMenuOpen = currentSection === name && Boolean(menuAnchor);
    const hideMenu = name === "Direct Messages" || name === "Groups";
    return (
      <div
        className="titleGroupDiv"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
      >
        <div className="titleGroup">{name.toUpperCase()}</div>
        {!hideMenu && (
          <IconButton
            size="small"
            aria-label={`${name} options`}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            onClick={(e) => handleMenuClick(name, e)}
            className="moreVertIconWrapper"
            sx={{
              opacity: isMenuOpen ? 0.7 : 0,
              transition: 'opacity 0.2s ease',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <MoreVertIcon sx={{ fontSize: '1.1rem', color: nav.subPanelText }} />
          </IconButton>
        )}
      </div>
    );
  };

  const getMentionBadge = (hasMention: boolean) =>
    hasMention ? (
      <Badge
        badgeContent="@"
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: nav.badge,
            color: 'white',
            fontSize: '0.65rem',
            fontWeight: 700,
            minWidth: '18px',
            height: '18px',
            padding: '0 4px',
          }
        }}
      />
    ) : null;

  const getMessageCount = (count: number, unread: boolean) =>
    count > 0 ? (
      <Badge
        badgeContent={count}
        className={unread ? "" : "defaultBadge"}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: unread ? nav.badge : undefined,
            color: 'white',
            minWidth: '18px',
            height: '18px',
            borderRadius: '100px',
            fontSize: '12px',
            padding: '0 4px',
          }
        }}
      />
    ) : null;

  const getIconAvatar = (item: MessageItem) => {
    const memberAvatarUrl = !item.avatar
      ? item.memberUids?.reduce<string | null | undefined>((found, uid) => {
          return found ?? allUsersMap[uid.toLowerCase()]?.avatarUrl;
        }, null)
      : null;

    const avatarSrc = item.avatar || memberAvatarUrl;

    if (avatarSrc) {
      // Decorative: the adjacent ListItemText already announces item.name, so
      // naming the image here would make screen readers say it twice.
      return <img src={avatarSrc} alt="" className="avatarImage" />;
    }

    return (
      <Avatar
        sx={{
          width: 20,
          height: 20,
          fontSize: '10px',
          fontWeight: 500,
          fontFamily: "var(--app-font-family, 'Proxima Nova')",
          bgcolor: nav.subPanelAvatarBg,
          color: nav.subPanelText,
        }}
      >
        {(item.name?.[0] ?? '?').toUpperCase()}
      </Avatar>
    );
  };

  const memberColors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#E91E63', '#00BCD4'];
  const getColorForUid = (uid: string): string => {
    let hash = 0;
    for (let i = 0; i < uid.length; i++) hash = (hash * 31 + uid.charCodeAt(i)) & 0xffffffff;
    return memberColors[Math.abs(hash) % memberColors.length];
  };

  const getMemberInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (name[0] ?? '?').toUpperCase();
  };

  const getGroupIconAvatar = (item: MessageItem) => {
    const memberUids = item.memberUids || [];
    if (memberUids.length === 0) return getIconAvatar(item);

    const displayUids = memberUids.slice(0, 4);
    const count = displayUids.length;

    const getMemberData = (uid: string) => {
      const user = allUsersMap[uid.toLowerCase()];
      return {
        color: getColorForUid(uid),
        initial: getMemberInitials(user?.name || uid)[0] ?? '?',
      };
    };

    if (count === 1) {
      const { color, initial } = getMemberData(displayUids[0]);
      return (
        <Avatar sx={{ width: 20, height: 20, fontSize: '9px', fontWeight: 500, bgcolor: color, color: 'white' }}>
          {initial}
        </Avatar>
      );
    }

    return (
      <Box sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1.5px',
        bgcolor: 'white',
        flexShrink: 0,
      }}>
        {displayUids.map((uid, i) => {
          const { color, initial } = getMemberData(uid);
          return (
            <Box
              key={uid}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: color,
                color: 'white',
                fontSize: count === 2 ? '7px' : '6px',
                fontWeight: 500,
                fontFamily: "var(--app-font-family, 'Proxima Nova')",
                lineHeight: 1,
                gridColumn: count === 3 && i === 2 ? '1 / -1' : undefined,
              }}
            >
              {initial}
            </Box>
          );
        })}
      </Box>
    );
  };

  // Shared selected-state sx for both parent item rows and instance rows.
  // itemSelectedBg rather than tabSelectedBg: these rows keep subPanelText,
  // which cannot clear 4.5:1 on the background the white NavBar pill needs.
  const selectedItemSx = {
    backgroundColor: `${nav.itemSelectedBg} !important`,
    border: `1px solid ${nav.tabSelectedBorder}`,
    borderRadius: 'var(--nav-radius-sm)',
    '&:hover': {
      backgroundColor: `${nav.itemSelectedBg} !important`,
    },
  };

  const getListItems = () => {
    const displayMessages = (hideEmptySections
      ? messages.filter((group) =>
          group.name === "Direct Messages" || (group.items && group.items.length > 0)
        )
      : messages).filter((group) => group.name !== "Projects");

    return displayMessages.map((group, idx) => (
      /* Each group is one <li> of .leftPanelList so the <ul> only ever has <li>
         children — the Collapse below renders a <div>, which is legal inside an
         <li> but not inside a <ul>. The header ListItem therefore drops to a
         <div> to avoid nesting <li> in <li>. */
      <Box component="li" key={idx} sx={{ listStyle: 'none' }}>
        {/* Section header — hidden in assistant-only (Chat tab) mode */}
        {!assistantOnly && (
          <ListItem component="div" disablePadding sx={{ pt: idx === 0 ? 2 : 1 }}>
            {/* Heading wraps the toggle (ARIA APG disclosure pattern) rather
                than sitting inside it: a heading nested in a button is flattened
                into the button's name and never reaches the rotor, which is the
                whole point of marking these up. font/margin inherit so
                .titleGroup stays the sole source of visual styling. */}
            <Box component="h2" sx={{ m: 0, width: '100%', font: 'inherit' }}>
              <ListItemButton
                onClick={() => handleCollapse(idx)}
                aria-expanded={Boolean(menuOpen[idx])}
                sx={{
                  px: 3,
                  pb: 0.5,
                  pt: 0,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                }}
              >
                {/* disableTypography: getTitle returns a <div>, which MUI would
                    otherwise nest inside a <span> — invalid, and it would drop
                    the .titleGroup font rules under Typography's. */}
                <ListItemText disableTypography primary={getTitle(group.name)} />
                {menuOpen[idx]
                  ? <ExpandLess sx={{ color: nav.subPanelText, fontSize: '1.2rem' }} />
                  : <ExpandMore sx={{ color: nav.subPanelText, fontSize: '1.2rem' }} />
                }
              </ListItemButton>
            </Box>
          </ListItem>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              mt: 0.5,
              minWidth: '180px',
            }
          }}
        >
          {getMenuOptions(currentSection).map((opt) => {
            const IconComponent = opt === "Create Group" ? GroupsIcon : PersonIcon;
            return (
              <MenuItem
                key={opt}
                onClick={() => handleMenuAction(opt)}
                sx={{ py: 1.25, px: 2, gap: 1.5, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
              >
                <IconComponent sx={{ fontSize: '1.2rem', color: '#666' }} />
                {opt}
              </MenuItem>
            );
          })}
        </Menu>

        {group.items && group.items.length > 0 && (
          <Collapse in={assistantOnly ? true : menuOpen[idx]} timeout="auto" unmountOnExit>
            <List component="ul" disablePadding sx={{ px: 1, pt: assistantOnly ? 2 : 0 }}>
              {group.items.map((item, itemIdx) => (
                /* Same reason as the group wrapper: this <li> holds both the row
                   and its instances Collapse, keeping the <ul> free of divs. */
                <Box component="li" key={itemIdx} sx={{ listStyle: 'none' }}>
                  {/* Parent item row */}
                  <ListItem
                    component="div"
                    disablePadding
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getMentionBadge(item.hasMention || false)}
                        {getMessageCount(item.notificationCount, item.unread || false)}
                        {allowsMultipleInstances(item) && (
                          <Tooltip title="Show chats">
                            <IconButton
                              size="small"
                              aria-label={`${expandedInstances.has(getExpansionKey(item)) ? 'Hide' : 'Show'} chats in ${item.name}`}
                              aria-expanded={expandedInstances.has(getExpansionKey(item))}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleInstancesExpansion(getExpansionKey(item));
                              }}
                              sx={{ color: nav.subPanelTextMuted }}
                            >
                              {expandedInstances.has(getExpansionKey(item))
                                ? <ExpandLess fontSize="small" />
                                : <ChevronRightIcon fontSize="small" />
                              }
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemButton
                      selected={idSelected === item.collaborationId && !selectedInstanceId}
                      onClick={() => handleClickPanel(item, group.name)}
                      sx={{
                        gap: 1,
                        py: 0.5,
                        borderRadius: 'var(--nav-radius-sm)',
                        color: nav.subPanelText,
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                        '&.Mui-selected': selectedItemSx,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                        {group.name === "Groups" ? getGroupIconAvatar(item) : getIconAvatar(item)}
                      </ListItemIcon>
                      <ListItemText
                        disableTypography
                        primary={item.name}
                        secondary={
                          item.members && item.members.length > 0 ? (
                            <div>
                              Members:{" "}
                              {item.members.join(", ")}
                            </div>
                          ) : null
                        }
                      />
                    </ListItemButton>
                  </ListItem>

                  {/* Expandable instances list */}
                  {allowsMultipleInstances(item) && expandedInstances.has(getExpansionKey(item)) && (
                    <Collapse in timeout="auto" unmountOnExit>
                      {/* Real <ul>: every child below is a plain ListItem (<li>). */}
                      <List component="ul" disablePadding>
                        {/* + New Chat */}
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => handleCreateInstance(item, group.name)}
                            disabled={isCreatingInstance}
                            sx={{
                              pl: '48px',
                              py: 0.5,
                              borderRadius: 'var(--nav-radius-sm)',
                              color: nav.link,
                              '&:hover': { backgroundColor: 'rgba(58,138,247,0.08)' },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 'auto', mr: 0.75 }}>
                              <AddIcon sx={{ fontSize: '1rem', color: nav.link }} />
                            </ListItemIcon>
                            <ListItemText
                              primary="New Chat"
                              primaryTypographyProps={{
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: "var(--app-font-family, 'Proxima Nova')",
                                color: nav.link,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>

                        {/* Instance rows */}
                        {(() => {
                          const expansionKey = getExpansionKey(item);
                          const instances = (item.collab_instances || []).filter(inst => inst.status === 'active');
                          const visibleCount = visibleInstancesCount[expansionKey] || 5;
                          const visibleInstances = instances.slice(0, visibleCount);
                          const hasMore = instances.length > visibleCount;

                          return (
                            <>
                              {visibleInstances.map((instance) => (
                                <ListItem
                                  key={instance.id}
                                  disablePadding
                                  /* Row actions stay mounted (so they are reachable by
                                     Tab) and are revealed on hover OR keyboard focus.
                                     Previously they were mouse-hover-only, which put
                                     rename/delete out of reach for keyboard and touch.
                                     They must also collapse to zero width when hidden:
                                     the whole secondaryAction is right-anchored, so an
                                     invisible-but-still-measured actions box pushes the
                                     unread badge left over the chat title. display/
                                     visibility would drop them out of the tab order,
                                     hence width + overflow (which also zeroes the flex
                                     item's automatic min-width). */
                                  sx={{
                                    '& .lp-row-actions': {
                                      opacity: 0,
                                      width: 0,
                                      overflow: 'hidden',
                                      pointerEvents: 'none',
                                    },
                                    '&:hover .lp-row-actions, &:focus-within .lp-row-actions': {
                                      opacity: 1,
                                      width: 'auto',
                                      overflow: 'visible',
                                      pointerEvents: 'auto',
                                    },
                                    '&:hover .MuiListItemButton-root, &:focus-within .MuiListItemButton-root': {
                                      pr: '80px',
                                    },
                                  }}
                                  secondaryAction={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {draftGroupIds?.has(instance.id) && (
                                        <Tooltip title="Draft saved">
                                          <EditNoteIcon sx={{ fontSize: '1rem', color: nav.subPanelTextMuted }} />
                                        </Tooltip>
                                      )}
                                      {(instance.unreadCount ?? 0) > 0 && (
                                        <Badge
                                          badgeContent={instance.unreadCount}
                                          sx={{
                                            '& .MuiBadge-badge': {
                                              backgroundColor: nav.badge,
                                              color: 'white',
                                              minWidth: '18px',
                                              height: '18px',
                                              borderRadius: '100px',
                                              fontSize: '12px',
                                              position: 'relative',
                                              transform: 'none',
                                              transformOrigin: 'unset',
                                            }
                                          }}
                                        />
                                      )}
                                      <Box className="lp-row-actions" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Tooltip title="Rename">
                                          <IconButton
                                            size="small"
                                            aria-label={`Rename ${instance.topic || 'New chat'}`}
                                            onClick={(e) => handleOpenRenameDialog(e, instance, item, group.name)}
                                            sx={{ padding: '2px', color: nav.subPanelTextMuted, '&:hover': { color: nav.subPanelText, backgroundColor: 'transparent' } }}
                                          >
                                            <EditIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                          <IconButton
                                            size="small"
                                            aria-label={`Delete ${instance.topic || 'New chat'}`}
                                            onClick={(e) => handleOpenDeleteDialog(e, instance, item, group.name)}
                                            sx={{ padding: '2px', color: nav.subPanelTextMuted, '&:hover': { color: nav.subPanelText, backgroundColor: 'transparent' } }}
                                          >
                                            <DeleteOutlineIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Box>
                                  }
                                >
                                  <ListItemButton
                                    selected={selectedInstanceId === instance.id}
                                    onClick={() => handleInstanceClick(item, instance, group.name)}
                                    sx={{
                                      pl: '48px',
                                      pr: '36px',
                                      py: 0.5,
                                      borderRadius: 'var(--nav-radius-sm)',
                                      color: nav.subPanelText,
                                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                                      '&.Mui-selected': selectedItemSx,
                                    }}
                                  >
                                    <ListItemText
                                      primary={instance.topic || 'New chat'}
                                      primaryTypographyProps={{
                                        fontSize: '14px',
                                        fontWeight: selectedInstanceId === instance.id ? 600 : 400,
                                        fontFamily: "var(--app-font-family, 'Proxima Nova')",
                                        color: nav.subPanelText,
                                        noWrap: true,
                                        sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              ))}
                              {hasMore && (
                                <ListItem disablePadding>
                                  <ListItemButton
                                    onClick={() =>
                                      setVisibleInstancesCount(prev => ({
                                        ...prev,
                                        [expansionKey]: visibleCount + 5,
                                      }))
                                    }
                                    sx={{
                                      pl: '48px',
                                      py: 0.5,
                                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                                    }}
                                  >
                                    <ListItemText
                                      primary="Show more"
                                      primaryTypographyProps={{
                                        fontSize: '0.8rem',
                                        color: nav.subPanelTextMuted,
                                        textAlign: 'center',
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              )}
                            </>
                          );
                        })()}
                      </List>
                    </Collapse>
                  )}
                </Box>
              ))}
            </List>
          </Collapse>
        )}

        {/* + New Direct Message — only in Direct Messages section, after all items.
            Hidden in assistant-only (Chat tab) mode; users start a conversation via
            each assistant's own "+ New Chat". */}
        {group.name === "Direct Messages" && menuOpen[idx] && !assistantOnly && (
          <ListItemButton
            onClick={() => onStartNewDM?.()}
            sx={{
              pl: '24px',
              py: 0.5,
              mx: 1,
              borderRadius: 'var(--nav-radius-sm)',
              color: nav.link,
              '&:hover': { backgroundColor: 'rgba(58,138,247,0.08)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', mr: 0.75 }}>
              <AddIcon sx={{ fontSize: '1rem', color: nav.link }} />
            </ListItemIcon>
            <ListItemText
              primary="New Direct Message"
              primaryTypographyProps={{
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: "var(--app-font-family, 'Proxima Nova')",
                color: nav.link,
              }}
            />
          </ListItemButton>
        )}

        {/* + New Group — only in Groups section, after all items */}
        {group.name === "Groups" && menuOpen[idx] && (
          <ListItemButton
            onClick={() => onStartNewGroup?.()}
            sx={{
              pl: '24px',
              py: 0.5,
              mx: 1,
              borderRadius: 'var(--nav-radius-sm)',
              color: nav.link,
              '&:hover': { backgroundColor: 'rgba(58,138,247,0.08)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', mr: 0.75 }}>
              <AddIcon sx={{ fontSize: '1rem', color: nav.link }} />
            </ListItemIcon>
            <ListItemText
              primary="New Group"
              primaryTypographyProps={{
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: "var(--app-font-family, 'Proxima Nova')",
                color: nav.link,
              }}
            />
          </ListItemButton>
        )}
      </Box>
    ));
  };

  /* Placeholders only — deliberately plain Boxes rather than ListItemButtons.
     As buttons they were focusable and exposed as unnamed ARIA commands (their
     only content is a Skeleton), so keyboard users could tab into a stack of
     anonymous controls that do nothing. The list is aria-hidden while loading
     and the status below carries the announcement instead. */
  const renderSkeletonItems = () =>
    [0, 1, 2].map((sectionIdx) => (
      /* Mirrors the real list's nesting so the skeleton is valid markup too. */
      <Box component="li" key={`skeleton-section-${sectionIdx}`} sx={{ listStyle: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1 }}>
          <Skeleton variant="text" width="60%" sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        </Box>
        <Collapse in timeout="auto" unmountOnExit>
          <List component="ul" disablePadding>
            {[0, 1, 2].map((itemIdx) => (
              <Box
                component="li"
                key={`skeleton-item-${itemIdx}`}
                sx={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: 1.5, pl: 3, py: 1 }}
              >
                <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                <Skeleton variant="text" width="80%" sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Box>
            ))}
          </List>
        </Collapse>
      </Box>
    ));

  return (
    <>
      {/* Wrapper rather than List component="nav": the ListItem children render
          as <li>, which would be invalid directly inside a <nav>. The wrapper
          carries height:100% so .leftPanelList's own height:100% still resolves
          against a definite height and keeps its scroll container. */}
      <nav aria-label="Conversations" className="leftPanelNav">
        {/* Skeletons carry no information, so they are kept out of the a11y
            tree and this status announces the load instead. */}
        <Box component="p" role="status" sx={srOnly}>
          {isLoading ? 'Loading conversations' : ''}
        </Box>
        <List disablePadding className="leftPanelList" aria-hidden={isLoading || undefined}>
          {isLoading ? renderSkeletonItems() : getListItems()}
        </List>
      </nav>

      {dialogOpen && (
        <InputDialog
          open
          title={`Create ${currentSection}`}
          label="Name"
          onSubmit={handleDialogSubmit}
          onClose={() => setDialogOpen(false)}
          hideNameInput={currentSection === "Direct Messages"}
        />
      )}

      <EditDialog
        open={renameDialogOpen}
        title="Rename Chat"
        name={instanceToRename?.instance.topic || ''}
        onConfirm={handleRenameInstance}
        onClose={() => {
          setRenameDialogOpen(false);
          setInstanceToRename(null);
        }}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Chat"
        message={`Are you sure you want to delete "${instanceToDelete?.instance.topic || 'this chat'}"? This action cannot be undone.`}
        color="error"
        onConfirm={handleDeleteInstance}
        onClose={() => {
          setDeleteDialogOpen(false);
          setInstanceToDelete(null);
        }}
      />

      <Notification
        {...notif}
        onClose={() => setNotif({ ...notif, open: false })}
      />
    </>
  );
};

export default LeftPanel;
